import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiCall from '../../utils/api';
import Menu from './Menu';
import localisationIcon from '../../assets/localisation_manager.png';
import loupeGriseIcon from '../../assets/loupe_grise.png';
import loupeBleuIcon from '../../assets/loupe.png';
import UpdateCardComponent from './UpdateCard';
import CreateCardComponent from './CreateCard';
import plusIcon from '../../assets/plus_blanc.png';
import boxViewIcon from '../../assets/box_view.png';
import tableViewIcon from '../../assets/table_view.png';
import boxWhiteViewIcon from '../../assets/box_white_view.png';
import tableWhiteViewIcon from '../../assets/table_white_view.png';
import checkIcon from '../../assets/true_icon.png';
import extendIcon from '../../assets/extend.png';
import reduceIcon from '../../assets/reduce.png';
import { domainIconMapRaw } from '../Onboarding/domainIconMap';
import {
  ContentContainer,
  TopActionContainer,
  SearchAndAddContainer,
  ViewToggleContainer,
  ViewToggleButton,
  SearchWrapper,
  SearchInput,
  SearchIconWrapper,
  SearchIcon,
  FormationsGrid,
  FormationCard,
  CardTopContent,
  FormationTitle,
  EstablishmentInfo,
  LocationIcon,
  EstablishmentName,
  ErrorMessage,
  LoadingMessage,
  UpdateButton,
  AddButton,
  TableContainer,
  Table,
  TableHeader,
  TableHeaderRow,
  ExpandIcon,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TypeFinalTableCell,
  TypeFinalHeaderCell,
  TypeSelectionDropdown,
  TypeCheckboxGroup,
  TypeCheckboxItem,
  TypeCheckboxLabel,
  TypeCheckboxInput,
  TypeSelectionTrigger,
  EditableTableCell,
  SuccessIndicator,
  ToggleSwitch,
  ToggleCircle,
  DomainSelectionDropdown,
  DomainCheckboxGroup,
  DomainCheckboxItem,
  DomainCheckboxLabel,
  DomainCheckboxInput,
  DomainSelectionTrigger,
  BinaryCell,
  BinarySelect,
  BinaryValue
} from './UpdateStyles';

// Define domainOptions from domainIconMapRaw
const domainOptions = Object.keys(domainIconMapRaw);

const formationTypeOptions = [
  "Grande école post bac",
  "BTS",
  "Formations préparatoires à l'enseignement supérieur",
  "BUT",
  "C.M.I",
  "CPGE",
  "DEUST",
  "CS",
  "CUPGE",
  "Licence (L1)"
];

const Formations = () => {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [managerUAI, setManagerUAI] = useState('');
  const [viewMode, setViewMode] = useState('table'); // Changed from 'box' to 'table'
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [successCell, setSuccessCell] = useState(null);
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [selectedTypesMap, setSelectedTypesMap] = useState({});
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null);
  const [selectedPrincipalDomainsMap, setSelectedPrincipalDomainsMap] = useState({});
  const [selectedSecondaryDomainsMap, setSelectedSecondaryDomainsMap] = useState({});
  const [openPrincipalDomainDropdown, setOpenPrincipalDomainDropdown] = useState(null);
  const [openSecondaryDomainDropdown, setOpenSecondaryDomainDropdown] = useState(null);
  const navigate = useNavigate();

  const loadFormations = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFormations([]);
    setEstablishments([]);

    try {
      // Fetch both formations and establishments in parallel to ensure fresh data
      const [formationsResponse, establishmentsResponse] = await Promise.all([
        apiCall(`${process.env.REACT_APP_BACKEND_URL}/manager/my-formations/`, 'GET'),
        apiCall(`${process.env.REACT_APP_BACKEND_URL}/manager/my-establishments/`, 'GET')
      ]);

      // Process establishments
      if (establishmentsResponse && establishmentsResponse.length > 0) {
        setEstablishments(establishmentsResponse);
        setManagerUAI(establishmentsResponse[0].uai); // Set UAI from fresh data
      } else {
        setManagerUAI('');
        setEstablishments([]);
      }

      // Process formations
      if (Array.isArray(formationsResponse)) {
        setFormations(formationsResponse);
        if (formationsResponse.length === 0) {
            if (!establishmentsResponse || establishmentsResponse.length === 0) {
                setError('Aucun établissement associé à ce compte manager.');
            } else {
                setError('No formations found for this establishment. You can add one using the button above.');
            }
        } else {
          setError(null); // Clear error if formations are loaded
        }
      } else {
        throw new Error("Unexpected response format for formations from backend");
      }

    } catch (err) {
      console.error('Error fetching formations data:', err);
      setError('Failed to fetch formations. Please try again.');
      setFormations([]);
      setEstablishments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormations();
  }, [loadFormations]);

  useEffect(() => {
    // Initialize selectedTypesMap from formations data
    const initialSelectedTypesMap = {};
    const initialPrincipalDomainsMap = {};
    const initialSecondaryDomainsMap = {};
    
    formations.forEach(formation => {
      if (formation.type_final) {
        initialSelectedTypesMap[formation.id] = formation.type_final.split(',').map(t => t.trim()).filter(Boolean);
      } else {
        initialSelectedTypesMap[formation.id] = [];
      }
      
      if (formation.domaines_principaux) {
        initialPrincipalDomainsMap[formation.id] = Array.isArray(formation.domaines_principaux) 
          ? formation.domaines_principaux 
          : formation.domaines_principaux.split(',').map(d => d.trim()).filter(Boolean);
      } else {
        initialPrincipalDomainsMap[formation.id] = [];
      }
      
      if (formation.domaines_secondaires) {
        initialSecondaryDomainsMap[formation.id] = Array.isArray(formation.domaines_secondaires) 
          ? formation.domaines_secondaires 
          : formation.domaines_secondaires.split(',').map(d => d.trim()).filter(Boolean);
      } else {
        initialSecondaryDomainsMap[formation.id] = [];
      }
    });
    
    setSelectedTypesMap(initialSelectedTypesMap);
    setSelectedPrincipalDomainsMap(initialPrincipalDomainsMap);
    setSelectedSecondaryDomainsMap(initialSecondaryDomainsMap);
  }, [formations]);

  const handleNavigateToUpdate = (formationId) => {
    if (formationId) {
        navigate(`/manager-edit-formation/${formationId}`);
    } else {
        console.error("Cannot navigate: Formation ID is missing");
    }
  };

  const normalizeString = (str) => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };

  const filteredFormations = formations.filter(formation => {
    const normalizedSearchTerm = normalizeString(searchTerm);
    if (!normalizedSearchTerm) return true;

    const normalizedName = normalizeString(formation.formation_nom_long);
    const normalizedEstablishment = normalizeString(formation.etablissement_nom);

    return normalizedName.includes(normalizedSearchTerm) || normalizedEstablishment.includes(normalizedSearchTerm);
  });

  const handleUpdateClick = (formation) => {
    setSelectedFormation(formation);
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setIsCreating(true);
    setSelectedFormation(null);
  };

  const handleCancel = () => {
    setSelectedFormation(null);
    setIsCreating(false);
  };

  const handleCreateSuccess = () => {
    setIsCreating(false);
    loadFormations();
  };

  const handleConfirmUpdate = () => {
    if (selectedFormation && selectedFormation.id) {
      console.log("Confirming update for", selectedFormation.id);
    }
  };

  const handleViewToggle = (mode) => {
    setViewMode(mode);
  };

  const handleCellChange = async (formationId, field, value) => {
    if (!formationId) return;
    
    // For text fields, store the change in pendingChanges but don't save yet
    if (field === 'formation_nom_long' || 
        field === 'region' || field === 'departement' || field === 'commune') {
      setPendingChanges(prev => ({
        ...prev,
        [formationId]: {
          ...prev[formationId],
          [field]: value
        }
      }));
      return;
    }
    
    // For non-text fields (like dropdowns, checkboxes), save immediately
    saveCellChange(formationId, field, value);
  };

  const handleTypeCheckboxChange = (formationId, typeOption, checked) => {
    setSelectedTypesMap(prev => {
      const currentTypes = [...(prev[formationId] || [])];
      
      if (checked) {
        // Add type if checked and not already present
        if (!currentTypes.includes(typeOption)) {
          currentTypes.push(typeOption);
        }
      } else {
        // Remove type if unchecked
        const index = currentTypes.indexOf(typeOption);
        if (index !== -1) {
          currentTypes.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [formationId]: currentTypes
      };
    });
  };

  const handleTypeDropdownToggle = (formationId, event) => {
    event.stopPropagation();
    setOpenTypeDropdown(openTypeDropdown === formationId ? null : formationId);
  };

  const handleTypeDropdownClickOutside = (event) => {
    if (!event.target.closest('.type-selection-dropdown')) {
      setOpenTypeDropdown(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleTypeDropdownClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleTypeDropdownClickOutside);
    };
  }, []);

  const handlePrincipalDomainCheckboxChange = (formationId, domainOption, checked) => {
    setSelectedPrincipalDomainsMap(prev => {
      const currentDomains = [...(prev[formationId] || [])];
      
      if (checked) {
        // Add domain if checked and not already present
        if (!currentDomains.includes(domainOption)) {
          currentDomains.push(domainOption);
        }
      } else {
        // Remove domain if unchecked
        const index = currentDomains.indexOf(domainOption);
        if (index !== -1) {
          currentDomains.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [formationId]: currentDomains
      };
    });
  };
  
  const handleSecondaryDomainCheckboxChange = (formationId, domainOption, checked) => {
    setSelectedSecondaryDomainsMap(prev => {
      const currentDomains = [...(prev[formationId] || [])];
      
      if (checked) {
        // Add domain if checked and not already present
        if (!currentDomains.includes(domainOption)) {
          currentDomains.push(domainOption);
        }
      } else {
        // Remove domain if unchecked
        const index = currentDomains.indexOf(domainOption);
        if (index !== -1) {
          currentDomains.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [formationId]: currentDomains
      };
    });
  };
  
  const handlePrincipalDomainDropdownToggle = (formationId, event) => {
    event.stopPropagation();
    setOpenPrincipalDomainDropdown(openPrincipalDomainDropdown === formationId ? null : formationId);
  };
  
  const handleSecondaryDomainDropdownToggle = (formationId, event) => {
    event.stopPropagation();
    setOpenSecondaryDomainDropdown(openSecondaryDomainDropdown === formationId ? null : formationId);
  };
  
  const handleDomainDropdownClickOutside = (event) => {
    if (!event.target.closest('.domain-selection-dropdown')) {
      setOpenPrincipalDomainDropdown(null);
      setOpenSecondaryDomainDropdown(null);
    }
  };
  
  useEffect(() => {
    document.addEventListener('mousedown', handleDomainDropdownClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleDomainDropdownClickOutside);
    };
  }, []);

  const saveCellChange = async (formationId, field, value) => {
    if (!formationId) return;
    
    setSavingCell({ formationId, field });
    
    try {
      const formation = formations.find(f => f.id === formationId);
      if (!formation) return;
      
      const updatedFormation = { ...formation };
      const isListField = ['poursuite_etudes', 'debouches_professionnels'].includes(field);
      
      // Process value before assigning
      let processedValue = value;
      if (field === 'type_final') {
        processedValue = selectedTypesMap[formationId]?.join(', ') || '';
      } else if (field === 'domaines_principaux') {
        processedValue = selectedPrincipalDomainsMap[formationId] || [];
      } else if (field === 'domaines_secondaires') {
        processedValue = selectedSecondaryDomainsMap[formationId] || [];
      } else if (isListField && typeof value === 'string') {
        // --- Convert string from textarea back to array for list fields --- 
        processedValue = value.split('\n').map(s => s.trim()).filter(Boolean);
      } // else: Keep original value for other fields

      updatedFormation[field] = processedValue;
      
      // Update the formation in the database
      await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/formations/${formationId}/`,
        'PUT',
        updatedFormation
      );
      
      // Update the local state with the processed value
      setFormations(prevFormations => 
        prevFormations.map(f => 
          f.id === formationId ? { ...f, [field]: processedValue } : f
        )
      );
      
      // Clear pending changes for this field
      if (pendingChanges[formationId]) {
        const updatedPendingChanges = { ...pendingChanges };
        delete updatedPendingChanges[formationId][field];
        if (Object.keys(updatedPendingChanges[formationId]).length === 0) {
          delete updatedPendingChanges[formationId];
        }
        setPendingChanges(updatedPendingChanges);
      }
      
      // Show success message briefly
      setSuccess(`Champ "${field}" mis à jour avec succès`);
      setShowSuccessIndicator(true);
      setTimeout(() => {
        setSuccess(null);
        setShowSuccessIndicator(false);
      }, 4000);
    } catch (err) {
      console.error('Error updating formation field:', err);
      setError(`Erreur lors de la mise à jour du champ "${field}"`);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSavingCell(null);
    }
  };

  const handleCellKeyDown = (e, formationId, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value;
      saveCellChange(formationId, field, value);
      setEditingCell(null);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingCell(null);
    }
  };

  const handleCellBlur = (formationId, field) => {
    // Only save on blur for non-text fields
    if (field !== 'formation_nom_long' && field !== 'etablissement_nom' && 
        field !== 'region' && field !== 'departement' && field !== 'commune' && 
        field !== 'type_final') {
      const value = pendingChanges[formationId]?.[field] || formations.find(f => f.id === formationId)?.[field];
      if (value !== undefined) {
        saveCellChange(formationId, field, value);
      }
    }
    setEditingCell(null);
  };

  const handleCellFocus = (formationId, field) => {
    setEditingCell({ formationId, field });
  };

  const handleCellClick = (formationId, column) => {
    setEditingCell({ id: formationId, column });
    setEditingValue(formations.find(f => f.id === formationId)[column] || '');
  };

  const handleSaveCell = async (formationId, column, value) => {
    setSavingCell({ id: formationId, column });
    try {
      await saveCellChange(formationId, column, value);
      setSuccessCell({ id: formationId, column });
      setTimeout(() => setSuccessCell(null), 2000);
    } catch (error) {
      console.error('Error saving cell:', error);
    } finally {
      setSavingCell(null);
      setEditingCell(null);
    }
  };

  const renderEditableCell = (formation, column) => {
    const isEditing = editingCell?.id === formation.id && editingCell?.column === column;
    const isSaving = savingCell?.id === formation.id && savingCell?.column === column;
    const isSuccess = successCell?.id === formation.id && successCell?.column === column;

    // --- Handle Boolean Toggles (Apprentissage/Internat) ---
    if (column === 'apprentissage' || column === 'internat') {
      const isActive = formation[column] === true || String(formation[column]).toLowerCase() === 'true';
      
      return (
        <TableCell style={{ textAlign: 'center' }}>
          {isSaving ? (
            <span style={{ color: '#aaa' }}>...</span>
          ) : (
            <ToggleSwitch 
              isActive={isActive}
              onClick={(e) => {
                e.stopPropagation();
                const newValue = !isActive;
                setFormations(prev => prev.map(f => f.id === formation.id ? {...f, [column]: newValue} : f));
                saveCellChange(formation.id, column, newValue.toString()); 
              }}
            >
              <ToggleCircle isActive={isActive} />
            </ToggleSwitch>
          )}
        </TableCell>
      );
    }

    // --- Handle other Boolean Selects (is_public etc.) - Assuming these are always editable for now ---
    if (['is_public', 'is_visible', 'is_visible_public'].includes(column)) {
      return (
        <TableCell>
          <BinaryCell
            onClick={() => handleCellClick(formation.id, column)}
            isEditing={isEditing}
            isSaving={isSaving}
            isSuccess={isSuccess}
            style={{ textAlign: 'center' }}
          >
            {isEditing ? (
              <BinarySelect
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCell(formation.id, column, editingValue);
                  }
                }}
                onBlur={() => handleSaveCell(formation.id, column, editingValue)}
              >
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
              </BinarySelect>
            ) : (
              <BinaryValue isTrue={formation[column]}>
                {formation[column] ? 'Oui' : 'Non'}
              </BinaryValue>
            )}
          </BinaryCell>
        </TableCell>
      );
    }

    const placeholderStrings = ['PANEL_BAC_NOT_FOUND', 'NO_BADGES_FOUND_or_not_list', null, undefined, ''];
    
    // --- Handle Moyenne Fields (Conditional Editability) ---
    const moyenneColumns = ['moyenne_formation_general', 'moyenne_formation_technologique', 'moyenne_formation_professionnel'];
    if (moyenneColumns.includes(column)) {
        const originalValue = formation[column];
        const isReadOnly = !placeholderStrings.includes(originalValue);
        if (isReadOnly) {
            const num = parseFloat(originalValue);
            const displayMoyenne = !isNaN(num) ? num.toFixed(2) : '-';
            return (
                <TableCell style={{ textAlign: 'right', paddingRight: '1rem' }}>
                   {displayMoyenne} {displayMoyenne !== '-' && <span style={{ color: 'currentColor' }}>🔒</span>}
                </TableCell>
            );
        }
        // Fall through for editable moyenne fields
    }

    // --- Handle Bac % Fields (Conditional Editability) ---
    const bacColumns = ['bac_general', 'bac_technologique', 'bac_professionnel'];
    if (bacColumns.includes(column)) {
        const originalValue = formation[column];
        const isReadOnly = !placeholderStrings.includes(originalValue);
        if (isReadOnly) {
            // Display the existing value (it's already a percentage string)
            return (
                <TableCell style={{ textAlign: 'right', paddingRight: '1rem' }}>
                    {originalValue} % {originalValue !== '-' && <span style={{ color: 'currentColor' }}>🔒</span>}
                </TableCell>
            );
        }
        // Fall through for editable bac fields
    }

    // --- Default Editable Cell Logic ---
    let displayValue = formation[column];
    const isListField = ['poursuite_etudes', 'debouches_professionnels'].includes(column);

    // Use placeholder only if the value is considered a placeholder
    if (placeholderStrings.includes(displayValue)) {
        displayValue = '-'; 
    } else if (isListField && Array.isArray(displayValue) && !isEditing) {
        // --- Display list fields as comma-separated when not editing --- 
        displayValue = displayValue.join(', ');
    }

    return (
      <EditableTableCell 
        onClick={() => !isEditing && handleCellClick(formation.id, column)} 
        isEditing={isEditing}
        isSaving={isSaving}
        isSuccess={isSuccess}
        style={{ cursor: 'pointer' }}
      >
        {isEditing ? (
          // Use textarea for list fields to allow multi-line input
          isListField ? (
            <textarea 
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { // Save on Enter unless Shift is pressed
                  e.preventDefault();
                  handleSaveCell(formation.id, column, editingValue);
                }
                if (e.key === 'Escape') {
                    setEditingCell(null);
                }
              }}
              onBlur={() => handleSaveCell(formation.id, column, editingValue)} 
              autoFocus
              style={{ width: '100%', minHeight: '60px', resize: 'vertical', fontFamily: 'inherit', fontSize: 'inherit', border: 'none', outline: 'none' }} // Basic styling
            />
          ) : (
            <input 
                type={typeof formation[column] === 'number' ? 'number' : 'text'} 
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCell(formation.id, column, editingValue);
                  }
                  if (e.key === 'Escape') {
                      setEditingCell(null);
                  }
                }}
                onBlur={() => handleSaveCell(formation.id, column, editingValue)} 
                autoFocus
             />
          )
        ) : (
          <span className="display-value">{displayValue}</span> 
        )}
      </EditableTableCell>
    );
  };

  const renderTypeFinalCell = (formation) => {
    const isEditing = editingCell && 
                     editingCell.formationId === formation.id && 
                     editingCell.field === 'type_final';
    
    const isSaving = savingCell && 
                    savingCell.formationId === formation.id && 
                    savingCell.field === 'type_final';
    
    const selectedTypes = selectedTypesMap[formation.id] || [];
    const isDropdownOpen = openTypeDropdown === formation.id;
    
    if (isEditing) {
        return (
        <TypeFinalTableCell>
          <div className="type-selection-dropdown" style={{ position: 'relative' }}>
            <TypeSelectionTrigger onClick={(e) => handleTypeDropdownToggle(formation.id, e)}>
              {selectedTypes.length > 0 ? selectedTypes.join(', ') : 'Sélectionner...'}
            </TypeSelectionTrigger>
            
            {isDropdownOpen && (
              <TypeSelectionDropdown>
                <TypeCheckboxGroup>
                  {formationTypeOptions.map(typeOption => (
                    <TypeCheckboxItem key={typeOption}>
                      <TypeCheckboxInput
                        id={`type-${formation.id}-${typeOption}`}
                        value={typeOption}
                        checked={selectedTypes.includes(typeOption)}
                        onChange={(e) => handleTypeCheckboxChange(formation.id, typeOption, e.target.checked)}
                      />
                      <TypeCheckboxLabel htmlFor={`type-${formation.id}-${typeOption}`}>
                        {typeOption}
                      </TypeCheckboxLabel>
                    </TypeCheckboxItem>
                  ))}
                </TypeCheckboxGroup>
              </TypeSelectionDropdown>
            )}
          </div>
        </TypeFinalTableCell>
        );
      } else {
        return (
        <TypeFinalTableCell 
          onClick={() => handleCellFocus(formation.id, 'type_final')}
          style={{ cursor: 'pointer' }}
        >
          {isSaving ? (
            <span style={{ color: '#aaa' }}>Sauvegarde...</span>
          ) : (
            selectedTypes.length > 0 ? selectedTypes.join(', ') : '-'
          )}
        </TypeFinalTableCell>
      );
    }
  };

  const renderPrincipalDomainsCell = (formation) => {
    const isEditing = editingCell && 
                     editingCell.formationId === formation.id && 
                     editingCell.field === 'domaines_principaux';
    
    const isSaving = savingCell && 
                    savingCell.formationId === formation.id && 
                    savingCell.field === 'domaines_principaux';
    
    const selectedDomains = selectedPrincipalDomainsMap[formation.id] || [];
    const isDropdownOpen = openPrincipalDomainDropdown === formation.id;
    
    if (isEditing) {
      return (
        <TableCell>
          <div className="domain-selection-dropdown" style={{ position: 'relative' }}>
            <DomainSelectionTrigger onClick={(e) => handlePrincipalDomainDropdownToggle(formation.id, e)}>
              {selectedDomains.length > 0 ? selectedDomains.join(', ') : 'Sélectionner...'}
            </DomainSelectionTrigger>
            
            {isDropdownOpen && (
              <DomainSelectionDropdown>
                <DomainCheckboxGroup>
                  {[...domainOptions].sort().map(domainOption => (
                    <DomainCheckboxItem key={domainOption}>
                      <DomainCheckboxInput
                        id={`principal-domain-${formation.id}-${domainOption}`}
                        value={domainOption}
                        checked={selectedDomains.includes(domainOption)}
                        onChange={(e) => handlePrincipalDomainCheckboxChange(formation.id, domainOption, e.target.checked)}
                      />
                      <DomainCheckboxLabel htmlFor={`principal-domain-${formation.id}-${domainOption}`}>
                        {domainOption}
                      </DomainCheckboxLabel>
                    </DomainCheckboxItem>
                  ))}
                </DomainCheckboxGroup>
              </DomainSelectionDropdown>
            )}
          </div>
        </TableCell>
      );
    } else {
      return (
        <TableCell 
          onClick={() => handleCellFocus(formation.id, 'domaines_principaux')}
          style={{ cursor: 'pointer' }}
        >
          {isSaving ? (
            <span style={{ color: '#aaa' }}>Sauvegarde...</span>
          ) : (
            selectedDomains.length > 0 ? selectedDomains.join(', ') : '-'
          )}
        </TableCell>
      );
    }
  };
  
  const renderSecondaryDomainsCell = (formation) => {
    const isEditing = editingCell && 
                     editingCell.formationId === formation.id && 
                     editingCell.field === 'domaines_secondaires';
    
    const isSaving = savingCell && 
                    savingCell.formationId === formation.id && 
                    savingCell.field === 'domaines_secondaires';
    
    const selectedDomains = selectedSecondaryDomainsMap[formation.id] || [];
    const isDropdownOpen = openSecondaryDomainDropdown === formation.id;
    
    if (isEditing) {
      return (
        <TableCell>
          <div className="domain-selection-dropdown" style={{ position: 'relative' }}>
            <DomainSelectionTrigger onClick={(e) => handleSecondaryDomainDropdownToggle(formation.id, e)}>
              {selectedDomains.length > 0 ? selectedDomains.join(', ') : 'Sélectionner...'}
            </DomainSelectionTrigger>
            
            {isDropdownOpen && (
              <DomainSelectionDropdown>
                <DomainCheckboxGroup>
                  {[...domainOptions].sort().map(domainOption => (
                    <DomainCheckboxItem key={domainOption}>
                      <DomainCheckboxInput
                        id={`secondary-domain-${formation.id}-${domainOption}`}
                        value={domainOption}
                        checked={selectedDomains.includes(domainOption)}
                        onChange={(e) => handleSecondaryDomainCheckboxChange(formation.id, domainOption, e.target.checked)}
                      />
                      <DomainCheckboxLabel htmlFor={`secondary-domain-${formation.id}-${domainOption}`}>
                        {domainOption}
                      </DomainCheckboxLabel>
                    </DomainCheckboxItem>
                  ))}
                </DomainCheckboxGroup>
              </DomainSelectionDropdown>
            )}
          </div>
        </TableCell>
      );
    } else {
      return (
        <TableCell 
          onClick={() => handleCellFocus(formation.id, 'domaines_secondaires')}
          style={{ cursor: 'pointer' }}
        >
          {isSaving ? (
            <span style={{ color: '#aaa' }}>Sauvegarde...</span>
          ) : (
            selectedDomains.length > 0 ? selectedDomains.join(', ') : '-'
          )}
        </TableCell>
      );
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const renderTableView = () => {
    if (loading) return <LoadingMessage>Chargement des formations...</LoadingMessage>;
    if (error && !loading) return <ErrorMessage>{error}</ErrorMessage>;
    if (filteredFormations.length === 0) {
      return <p style={{ marginTop: '1rem' }}>Aucune formation trouvée {searchTerm ? 'pour cette recherche' : 'pour cet établissement'}.</p>;
    }

    return (
      <TableContainer isFullScreen={isFullScreen}>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>
                <ExpandIcon 
                  src={isFullScreen ? reduceIcon : extendIcon} 
                  alt={isFullScreen ? "Reduce" : "Expand"} 
                  onClick={toggleFullScreen}
                  title={isFullScreen ? "Exit full screen" : "Enter full screen"}
                />
                Nom de la formation
              </TableHeaderCell>
              <TableHeaderCell>Établissement</TableHeaderCell>
              <TableHeaderCell>Région</TableHeaderCell>
              <TableHeaderCell>Département</TableHeaderCell>
              <TableHeaderCell>Commune</TableHeaderCell>
              <TypeFinalHeaderCell>Type</TypeFinalHeaderCell>
              <TableHeaderCell>Durée</TableHeaderCell>
              <TableHeaderCell>Alternance</TableHeaderCell>
              <TableHeaderCell>Internat</TableHeaderCell>
              <TableHeaderCell>Prix Normal</TableHeaderCell>
              <TableHeaderCell>Prix Boursier</TableHeaderCell>
              <TableHeaderCell>Salaire médian</TableHeaderCell>
              <TableHeaderCell>Bac Général %</TableHeaderCell>
              <TableHeaderCell>Bac Techno %</TableHeaderCell>
              <TableHeaderCell>Bac Pro %</TableHeaderCell>
              <TableHeaderCell>Moyenne Générale</TableHeaderCell>
              <TableHeaderCell>Moyenne Techno</TableHeaderCell>
              <TableHeaderCell>Moyenne Pro</TableHeaderCell>
              <TableHeaderCell>Domaines Principaux</TableHeaderCell>
              <TableHeaderCell>Domaines Secondaires</TableHeaderCell>
              <TableHeaderCell>Poursuite d'études</TableHeaderCell>
              <TableHeaderCell>Débouchés professionnels</TableHeaderCell>
              <TableHeaderCell>Lien Formation</TableHeaderCell>
              <TableHeaderCell>Site Web Étab.</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {filteredFormations.map((formation, index) => (
              <TableRow key={formation.id || index}>
                {renderEditableCell(formation, 'formation_nom_long')}
                {renderEditableCell(formation, 'etablissement_nom')}
                {renderEditableCell(formation, 'region')}
                {renderEditableCell(formation, 'departement')}
                {renderEditableCell(formation, 'commune')}
                {renderTypeFinalCell(formation)}
                {renderEditableCell(formation, 'duree')}
                {renderEditableCell(formation, 'apprentissage')}
                {renderEditableCell(formation, 'internat')}
                {renderEditableCell(formation, 'prix_normal')}
                {renderEditableCell(formation, 'prix_boursier')}
                {renderEditableCell(formation, 'salaire_median')}
                {renderEditableCell(formation, 'bac_general')}
                {renderEditableCell(formation, 'bac_technologique')}
                {renderEditableCell(formation, 'bac_professionnel')}
                {renderEditableCell(formation, 'moyenne_formation_general')}
                {renderEditableCell(formation, 'moyenne_formation_technologique')}
                {renderEditableCell(formation, 'moyenne_formation_professionnel')}
                {renderPrincipalDomainsCell(formation)}
                {renderSecondaryDomainsCell(formation)}
                {renderEditableCell(formation, 'poursuite_etudes')}
                {renderEditableCell(formation, 'debouches_professionnels')}
                {renderEditableCell(formation, 'link')}
                {renderEditableCell(formation, 'site_web')}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderBoxView = () => {
    if (loading) return <LoadingMessage>Chargement des formations...</LoadingMessage>;
    if (error && !loading) return <ErrorMessage>{error}</ErrorMessage>;
    if (filteredFormations.length === 0) {
      return <p style={{ marginTop: '1rem' }}>Aucune formation trouvée {searchTerm ? 'pour cette recherche' : 'pour cet établissement'}.</p>;
    }

    return (
      <FormationsGrid>
        {filteredFormations.map((formation, index) => (
          <FormationCard key={formation.id || index}>
            <CardTopContent>
              <FormationTitle>{formation.formation_nom_long || 'Nom indisponible'}</FormationTitle>
              <EstablishmentInfo>
                <LocationIcon src={localisationIcon} alt="Location" />
                <EstablishmentName>{formation.etablissement_nom || 'Établissement indisponible'}</EstablishmentName>
              </EstablishmentInfo>
            </CardTopContent>
            
            <UpdateButton 
                onClick={() => handleUpdateClick(formation)} 
                disabled={!formation.id}
            >
              Mettre à jour
            </UpdateButton>
          </FormationCard>
        ))}
      </FormationsGrid>
    );
  };

  const renderContent = () => {
    if (isCreating) {
      return (
        <CreateCardComponent 
          managerUAI={managerUAI}
          onCancel={handleCancel}
          onCreateSuccess={handleCreateSuccess} 
        />
      );
    }

    if (selectedFormation) {
      return (
        <UpdateCardComponent 
          formation={selectedFormation}
          onCancel={handleCancel}
        />
      );
    }

    return (
      <>
        <TopActionContainer>
          <SearchAndAddContainer>
            <SearchWrapper>
              <SearchInput 
                type="text"
                placeholder="Rechercher par nom de formation ou d'établissement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIconWrapper isActive={searchTerm.length > 0}>
                <SearchIcon 
                  src={searchTerm.length > 0 ? loupeBleuIcon : loupeGriseIcon} 
                  alt="Search"
                />
              </SearchIconWrapper>
            </SearchWrapper>
            
            {managerUAI && ( 
               <AddButton onClick={handleAddClick}> 
                 <img src={plusIcon} alt="Add" /> 
                 Ajouter une formation 
               </AddButton> 
            )}
          </SearchAndAddContainer>
          
          <ViewToggleContainer>
            <ViewToggleButton 
              isActive={viewMode === 'box'} 
              onClick={() => handleViewToggle('box')}
              title="Vue par cartes"
            >
              <img src={viewMode === 'box' ? boxWhiteViewIcon : boxViewIcon} alt="Box View" />
            </ViewToggleButton>
            <ViewToggleButton 
              isActive={viewMode === 'table'} 
              onClick={() => handleViewToggle('table')}
              title="Vue tableau"
            >
              <img src={viewMode === 'table' ? tableWhiteViewIcon : tableViewIcon} alt="Table View" />
            </ViewToggleButton>
            {showSuccessIndicator && (
              <SuccessIndicator>
                <img src={checkIcon} alt="Success" />
                Enregistré
              </SuccessIndicator>
            )}
          </ViewToggleContainer>
        </TopActionContainer>
        
        {viewMode === 'box' ? renderBoxView() : renderTableView()}
      </>
    );
  };

  return (
    <Menu>
      <ContentContainer>
        {renderContent()}
      </ContentContainer>
    </Menu>
  );
};

export default Formations; 